<template>
  <div
    :class="[
      this.checkBreakfastMode
        ? 'v-card border white rounded-lg pt-0 mt-0 mb-2'
        : 'pt-4'
    ]"
  >
    <v-alert
      v-if="checkBreakfastMode"
      icon="mdi-calendar"
      type="info"
      color="primary"
      class="ma-0"
      dismissible
      @input="removeDay"
    >
      {{ this.day }}
    </v-alert>
    <!--start card empty message-->
    <v-sheet
      v-show="isCartEmpty"
      width="70%"
      color="transparent"
      class="mt-10 mb-14 grey--text text-center mx-auto justify-center"
    >
      <v-icon large>mdi-shopping</v-icon>
      <p class="caption">
        {{$t("generic.lang_addItemMsg")}}
      </p>
    </v-sheet>
    <!--end card empty message-->
    <v-sheet
      v-show="!isCartEmpty"
      v-for="(order, index) in !checkBreakfastMode
        ? visibleCart
        : visibleCart.filter(item => item.date === day)"
      :key="index"
      color="transparent"
      :class="[
        checkBreakfastMode
          ? 'rounded-lg my-3 pa-2'
          : 'rounded-lg my-3 pa-0'
      ]"
    >
      <div class="d-flex justify-space-between">
        <div class="d-flex">
          <div class="text-left justify-start">
            <span class="font-weight-regular body-2 accent--text">
              {{ order.amount }}x
            </span>
          </div>

          <div class="ml-3">
            <div class="text-left justify-start">
              <span class="font-weight-bold caption" style="z-index: 1;">
                {{ order.name }}
              </span>
              <!-- Deposit -->
              <div v-if="enableDeposit && itemDeposit(order) > 0" class="text-muted caption">
                {{ $t("delivery.lang_includeDeposit") }} {{ (itemDeposit(order) * order.amount) | currency }}
              </div>
            </div>

            <!--start selected extras-->
            <div
              v-if="Array.isArray(order.garnish) && order.garnish.length > 0"
              class="text-left justify-start small"
              style="margin-left: -12px;"
            >
              <ul>
                <li
                  v-for="(ext, index) in order.garnish"
                  :key="ext.id + '-' + index"
                >
                  <strong class="primary--text">{{ ext.amount }}</strong>
                  x {{ ext.name }} (<strong class="primary--text">
                    {{ (ext.amount * ext.sell_price) | currency }} </strong
                  >)
                </li>
              </ul>
            </div>
            <!--end selected extras-->
          </div>
        </div>

        <!--start price amount-->
        <div class="mx-1">
          <span
            v-if="isPromoDiscounted(order)"
            class="caption grey--text text-decoration-line-through mr-1"
            style="z-index: 1;"
          >
            {{ promoOriginalTotal(order) | currency }}
          </span>
          <strong
            class="font-weight-bold caption"
            :class="isPromoDiscounted(order) ? 'success--text' : ''"
            style="z-index: 1;"
          >
            {{ itemTotalPrice(order) | currency }}
          </strong>
          <strong
            v-if="promoDiscountPercent(order) > 0"
            class="font-weight-bold caption success--text ml-1"
            style="z-index: 1;"
          >
            {{ "-" + promoDiscountPercent(order) + "%" }}
          </strong>
          <strong
            v-if="order.discount"
            class="font-weight-bold caption error--text"
            style="z-index: 1;"
          >
            {{ (-order.discount).toFixed(2) + "%" }}
          </strong>
        </div>
        <!--end price amount-->
      </div>

      <!--start cancelItem, openNote, plus and minus buttons-->
      <div class="d-flex justify-space-between align-center ml-5">
        <div class="d-flex">
          <v-btn @click="deleteItem(order)" icon small color="black">
            <v-icon small>mdi-trash-can-outline</v-icon>
          </v-btn>

          <v-btn @click="openNote(order)" icon small color="black">
            <v-icon small>mdi-comment-processing-outline</v-icon>
            <!--<font-awesome-icon :icon="['fal', 'CoffeeTogo']" size="2x" />-->
          </v-btn>
        </div>

        <div class="d-flex">
          <v-btn
            class="mr-1"
            color="accent"
            icon
            outlined
            rounded
            x-small
            :disabled="order.amount <= 1"
            @click="removeQTY(order)"
          >
            <v-icon class="pa-0 px-0" x-small>mdi-minus</v-icon>
          </v-btn>

          <v-btn
            color="accent"
            icon
            outlined
            rounded
            x-small
            @click="increaseQTY(order)"
          >
            <v-icon x-small>mdi-plus</v-icon>
          </v-btn>
        </div>
      </div>
      <!--end cancelItem, openNote, plus and minus buttons-->
    </v-sheet>
    <!--start note dialog-->
    <item-note-dialog
      v-if="!isCartEmpty"
      v-model="note_dialog"
      :dialog="note_dialog"
    />
    <!--end note dialog-->
  </div>
</template>

<script>
/*import plugins*/
import { createNamespacedHelpers } from "vuex";
import moment from "moment";

//import components
import ItemNoteDialog from "@/components/Card/ItemNoteDialog";
import { isBreakfast } from "../../helpers/breakfast";
import {
  isPromoDiscounted,
  isPromoItem,
  promoDiscountPercent,
  promoOriginalTotal
} from "@/utils/promoCode";

/*import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faCoffeeTogo, faPersonCarry } from "@fortawesome/pro-light-svg-icons";
library.add(faCoffeeTogo, faPersonCarry);*/

export default {
  name: "ShoppingCartOrdersCardPc",
  props: {
    day: {
      type: String,
      validator: val => moment(val, "DD.MM.YYYY").isValid()
    }
  },

  components: { ItemNoteDialog /*"font-awesome-icon": FontAwesomeIcon*/ },

  data: () => ({
    note_dialog: false
  }),

  computed: {
    /*reference Order getters*/
    ...createNamespacedHelpers("Order").mapGetters([
      "cart",
      "isCartEmpty",
      "itemTotalPrice",
      "itemDeposit"
    ]),
    ...createNamespacedHelpers("Config").mapGetters([
      "enableDeposit"
    ]),
    checkBreakfastMode() {
      return isBreakfast();
    },
    // the promo line is shown by PromoCodeRow, not as a cart row with qty buttons
    visibleCart() {
      return (this.cart || []).filter(order => !isPromoItem(order));
    }
  },

  methods: {
    isPromoDiscounted,
    promoDiscountPercent,
    promoOriginalTotal,
    ...createNamespacedHelpers("Order").mapMutations([
      "addQTY",
      "removeQTY",
      "deleteItem",
      "setSelectedItem",
      "replaceCart"
    ]),
    increaseQTY(order) {
      if (order && order.canBeSoldOncePerOrder) {
        this.$swal({
          title: this.$t("generic.lang_thisItemCanOnlyBeSoldOncePerOrder"),
          icon: "info"
        });

        return;
      }

      this.addQTY(order);
    },
    removeDay() {
      if (!this.isCartEmpty) {
        this.replaceCart([
          ...[...this.cart].filter(item => item.date !== this.day)
        ]);
      }
    },
    /*open note dialog*/
    openNote(item) {
      this.setSelectedItem(item);
      this.note_dialog = true;
    }

    /*saveFreetext() {
      this.$store
        .dispatch("cart/setItemFreetext", {
          item: this.cartItem,
          freetext: this.freetext,
        })
        .then(() => {
          this.freetext = "";
          this.editFreetext = false;
        });
    },*/
  }
};
</script>
